/**
 * Object Snapping System (OSnap)
 * 
 * Provides 2D/3D-style object snapping for precise drawing.
 * Supports: Endpoint, Midpoint, Center, Intersection, Perpendicular, etc.
 */

import * as THREE from 'three';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { settingsStorage } from '../../utils/settingsPersistence';

// ============================================================================
// TYPES
// ============================================================================

export type SnapType =
    | 'endpoint'      // Line/arc endpoints
    | 'midpoint'      // Midpoint of lines/arcs
    | 'center'        // Center of circles/arcs
    | 'node'          // Point objects
    | 'quadrant'      // Circle quadrants (0°, 90°, 180°, 270°)
    | 'intersection'  // Intersection of two objects
    | 'extension'     // Extension of lines
    | 'perpendicular' // Perpendicular to object
    | 'tangent'       // Tangent to circles/arcs
    | 'nearest'       // Nearest point on object
    | 'parallel';     // Parallel to reference line
    // No 'grid' mode: grid stepping is F9 (gridSnapEnabled), not an object
    // snap — a grid osnap candidate sat within aperture at virtually every
    // cursor position, so the marker and the magnet never rested.

export interface SnapPoint {
    type: SnapType;
    position: THREE.Vector3;
    entityId?: string;      // Source entity ID
    distance: number;       // Distance from cursor
    priority: number;       // Lower = higher priority
}

export interface SnapSettings {
    enabled: boolean;
    aperture: number;       // Snap aperture in pixels
    gridSize: number;       // Grid snap size in units
    /** F9 grid stepping (classic 2D default: off). */
    gridSnapEnabled: boolean;
    types: Record<SnapType, boolean>;
}

export interface SnapState extends SnapSettings {
    activeSnap: SnapPoint | null;
    candidates: SnapPoint[];
    /**
     * The world point the NEXT click will commit (osnap target when one is
     * acquired, otherwise the grid/polar/ortho-constrained cursor). Feeds
     * the statusbar coordinate readout so it always matches the marker and
     * the committed geometry. Transient — never persisted.
     */
    draftCursor: THREE.Vector3 | null;
    setEnabled: (enabled: boolean) => void;
    setAperture: (aperture: number) => void;
    setGridSize: (size: number) => void;
    setGridSnapEnabled: (enabled: boolean) => void;
    toggleGridSnap: () => void;
    toggleSnapType: (type: SnapType) => void;
    setSnapType: (type: SnapType, enabled: boolean) => void;
    enableOnly: (types: SnapType[]) => void;
    enableAll: () => void;
    disableAll: () => void;
    setActiveSnap: (snap: SnapPoint | null) => void;
    setCandidates: (candidates: SnapPoint[]) => void;
    setDraftCursor: (point: THREE.Vector3) => void;
}

// ============================================================================
// SNAP STORE
// ============================================================================

const defaultSnapTypes: Record<SnapType, boolean> = {
    endpoint: true,
    midpoint: true,
    center: true,
    node: true,
    quadrant: false,
    intersection: true,
    extension: false,
    perpendicular: true,
    tangent: false,
    nearest: false,
    parallel: false,
};

export const useSnapStore = create<SnapState>()(
    persist(
        (set) => ({
            enabled: true,
            aperture: 10,           // 10 pixel snap aperture (classic APERTURE default)
            gridSize: 0.5,          // 0.5 unit grid
            gridSnapEnabled: false, // F9 grid stepping (classic default: off)
            types: { ...defaultSnapTypes },
            activeSnap: null,
            candidates: [],
            draftCursor: null,

            setEnabled: (enabled) => set({ enabled }),
            setAperture: (aperture) => set({ aperture }),
            setGridSize: (gridSize) => set({ gridSize }),
            setGridSnapEnabled: (gridSnapEnabled) => set({ gridSnapEnabled }),
            toggleGridSnap: () => set((state) => ({ gridSnapEnabled: !state.gridSnapEnabled })),

            toggleSnapType: (type) => set((state) => ({
                types: { ...state.types, [type]: !state.types[type] }
            })),

            setSnapType: (type, enabled) => set((state) => ({
                types: { ...state.types, [type]: enabled }
            })),

            enableOnly: (types) => set((state) => {
                const newTypes = { ...state.types };
                Object.keys(newTypes).forEach((t) => {
                    newTypes[t as SnapType] = types.includes(t as SnapType);
                });
                return { types: newTypes };
            }),

            enableAll: () => set({
                types: Object.fromEntries(
                    Object.keys(defaultSnapTypes).map(t => [t, true])
                ) as Record<SnapType, boolean>
            }),

            disableAll: () => set({
                types: Object.fromEntries(
                    Object.keys(defaultSnapTypes).map(t => [t, false])
                ) as Record<SnapType, boolean>
            }),

            // Pointer-move storms recompute the best snap on every mousemove,
            // usually landing on the same snap target. Content-equal writes
            // are dropped so the SnapIndicator only re-renders when the
            // feedback actually changes.
            setActiveSnap: (activeSnap) => set((state) => {
                const current = state.activeSnap;
                if (current === activeSnap) return state;
                if (current && activeSnap
                    && current.type === activeSnap.type
                    && current.entityId === activeSnap.entityId
                    && current.position.distanceToSquared(activeSnap.position) < 1e-8) {
                    return state;
                }
                return { activeSnap };
            }),
            setCandidates: (candidates) => set({ candidates }),
            // Pointer storms resolve the same cursor point many times over;
            // clone + content-equal drop keep the StatusBar from re-rendering
            // when the readout has not actually moved.
            setDraftCursor: (point) => set((state) => {
                if (state.draftCursor
                    && state.draftCursor.distanceToSquared(point) < 1e-8) {
                    return state;
                }
                return { draftCursor: point.clone() };
            }),
        }),
        {
            name: 'yap-snap-settings',
            storage: settingsStorage,
            // Persist the snap preferences; activeSnap/candidates are
            // transient hover feedback and must not be restored.
            partialize: (state) => ({
                enabled: state.enabled,
                aperture: state.aperture,
                gridSize: state.gridSize,
                gridSnapEnabled: state.gridSnapEnabled,
                types: state.types,
            }),
        }
    )
);

// ============================================================================
// SNAP PRIORITY (lower = higher priority)
// ============================================================================

export const SNAP_PRIORITY: Record<SnapType, number> = {
    endpoint: 1,
    intersection: 2,
    midpoint: 3,
    center: 4,
    perpendicular: 5,
    tangent: 6,
    quadrant: 7,
    node: 8,
    nearest: 9,
    extension: 10,
    parallel: 11,
};

// ============================================================================
// SNAP ICONS (for UI display)
// ============================================================================

export const SNAP_ICONS: Record<SnapType, string> = {
    endpoint: '⬜',
    midpoint: '△',
    center: '○',
    node: 'ⓧ',
    quadrant: '◇',
    intersection: '✕',
    extension: '—',
    perpendicular: '⊥',
    tangent: '⌢',
    nearest: '∿',
    parallel: '∥',
};

// ============================================================================
// SNAP MANAGER CLASS
// ============================================================================

export class SnapManager {
    private entities: Map<string, EntitySnapData> = new Map();
    /**
     * Pre-computed snap points per entity (task: pointermove cost).
     * getEntitySnapPoints allocates Vector3s per shape per call; computing it
     * on every mousemove made the snap pass O(entity count) allocations per
     * event. Registration/unregistration invalidates the entry, so the cache
     * always mirrors the registered geometry and the hover loop becomes a
     * pure distance scan over reusable points.
     */
    private snapPointCache = new Map<string, SnapPoint[]>();
    private settings: SnapSettings;

    constructor(settings: SnapSettings) {
        this.settings = settings;
    }

    /**
     * Update settings
     */
    updateSettings(settings: SnapSettings): void {
        this.settings = settings;
    }

    /** Ids currently registered for entity snapping (for ledger diffing). */
    registeredIds(): string[] {
        return Array.from(this.entities.keys());
    }

    /**
     * Register an entity for snapping
     */
    registerEntity(id: string, data: EntitySnapData): void {
        this.entities.set(id, data);
        // Pre-compute snap points once: pointermove only scans distances.
        this.snapPointCache.set(id, this.computeEntitySnapPoints(data, id));
    }

    /**
     * Unregister an entity
     */
    unregisterEntity(id: string): void {
        this.entities.delete(id);
        this.snapPointCache.delete(id);
    }

    /**
     * Clear all registered entities
     */
    clearEntities(): void {
        this.entities.clear();
        this.snapPointCache.clear();
    }

    /**
     * Find snap points near cursor position.
     * `base` is the drawing base point (last committed point) when a
     * drafting command is mid-point: direction-dependent modes
     * (perpendicular) only produce candidates relative to it, matching the
     * classic OSNAP contract.
     */
    findSnapPoints(
        cursorWorld: THREE.Vector3,
        camera: THREE.Camera,
        renderer: THREE.WebGLRenderer,
        base?: THREE.Vector3 | null
    ): SnapPoint[] {
        if (!this.settings.enabled) return [];

        const snapPoints: SnapPoint[] = [];
        const apertureWorld = pixelsToWorld(
            this.settings.aperture,
            cursorWorld,
            camera,
            renderer
        );
        const apertureSq = apertureWorld * apertureWorld;

        // Scan the pre-computed snap points: no geometry re-tessellation and
        // no Vector3 allocation per pointer event, only squared distance tests.
        this.snapPointCache.forEach((entitySnaps) => {
            for (const snap of entitySnaps) {
                if (!this.settings.types[snap.type]) continue;
                const distanceSq = snap.position.distanceToSquared(cursorWorld);
                if (distanceSq <= apertureSq) {
                    snapPoints.push({
                        type: snap.type,
                        // Clone: cached positions are shared references; the
                        // best snap flows into tool state and may be mutated.
                        position: snap.position.clone(),
                        entityId: snap.entityId,
                        distance: Math.sqrt(distanceSq),
                        priority: snap.priority,
                    });
                }
            }
        });

        // Dynamic cursor-relative snaps: these depend on the live cursor and
        // on pairs of objects, so they cannot be pre-cached per entity.
        // Intersection, extension, perpendicular, tangent and nearest are
        // computed over the registered geometry against the current cursor.
        if (this.settings.types.intersection || this.settings.types.extension
            || this.settings.types.perpendicular || this.settings.types.tangent
            || this.settings.types.nearest) {
            this.collectDynamicSnaps(cursorWorld, apertureWorld, snapPoints, base);
        }

        // Sort by priority, then by distance
        return snapPoints.sort((a, b) => {
            if (a.priority !== b.priority) return a.priority - b.priority;
            return a.distance - b.distance;
        });
    }

    /**
     * Cursor-relative OSNAP pass. Tests line/curve pairs against each other
     * (intersection) and against the cursor (tangent point, nearest rim, line
     * extension ray). Appends candidates within the aperture to `out`.
     *
     * Perpendicular is base-aware: the candidate is the foot of the
     * perpendicular FROM the drawing base onto the line, valid while the
     * cursor hovers that foot. Without a base (idle hover, first click) the
     * mode is silent — hovering a line then never drags the point onto it.
     *
     * Broad phase (performance): every dynamic candidate lies ON a segment or
     * on a circle rim, so its distance to the cursor being ≤ aperture implies
     * the carrier object passes within `aperture` of the cursor. Each object
     * is therefore first tested with pure scalar math (no Vector3/Vector2
     * allocation, no pair loops); the O(L²) intersection passes run only over
     * the surviving near-subset — typically 0–5 objects, instead of every
     * line in the document times itself on every pointermove.
     */
    private collectDynamicSnaps(
        cursor: THREE.Vector3,
        aperture: number,
        out: SnapPoint[],
        base?: THREE.Vector3 | null
    ): void {
        const types = this.settings.types;
        const ap2 = aperture * aperture;
        // Ownership moves: helpers return fresh Vector3s, out keeps them.
        const push = (type: SnapType, position: THREE.Vector3, entityId: string) => {
            out.push({ type, position, entityId, distance: position.distanceTo(cursor), priority: SNAP_PRIORITY[type] });
        };

        // ---- Broad-phase filter: scalar-only passes over the ledger ----
        interface NearLine { id: string; a: THREE.Vector3; b: THREE.Vector3; foot: THREE.Vector3; footDistSq: number }
        interface NearCurve { id: string; center: THREE.Vector3; radius: number }
        const nearLines: NearLine[] = [];
        const nearCurves: NearCurve[] = [];
        // Extension needs the infinite ray past each endpoint: a cursor far
        // along the ray can be far from the segment yet exactly on it, so
        // extension candidates are collected with their own scalar test.
        const extensionLines: { id: string; a: THREE.Vector3; b: THREE.Vector3 }[] = [];

        const needNear = types.nearest || types.intersection;
        const needTangent = types.tangent;
        const needCurve = needNear || needTangent || types.intersection;
        const needLinePair = types.intersection;

        this.entities.forEach((data, id) => {
            if (data.type === 'line') {
                if (types.extension) extensionLines.push({ id, a: data.start, b: data.end });
                if (needLinePair || needNear) {
                    // Squared distance from cursor to segment, XY-plane with
                    // Z interpolated exactly like closestOnSegment3.
                    const a = data.start, b = data.end;
                    const abx = b.x - a.x, aby = b.y - a.y;
                    const len2 = abx * abx + aby * aby;
                    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((cursor.x - a.x) * abx + (cursor.y - a.y) * aby) / len2));
                    const fx = a.x + abx * t, fy = a.y + aby * t, fz = a.z + (b.z - a.z) * t;
                    const distSq = (fx - cursor.x) ** 2 + (fy - cursor.y) ** 2 + (fz - cursor.z) ** 2;
                    if (distSq <= ap2) {
                        nearLines.push({ id, a, b, foot: new THREE.Vector3(fx, fy, fz), footDistSq: distSq });
                    }
                }
            } else if (data.type === 'circle' || data.type === 'arc') {
                if (!needCurve) return;
                // Candidate points lie on the rim: |d(center) - r| ≤ aperture.
                const dx = cursor.x - data.center.x, dy = cursor.y - data.center.y;
                const d = Math.hypot(dx, dy);
                if (Math.abs(d - data.radius) <= aperture) {
                    nearCurves.push({ id, center: data.center, radius: data.radius });
                }
            }
        });

        // ---- Perpendicular: foot of the perpendicular from the base onto
        // each line (own pass — the foot may sit on the segment's extension,
        // outside the cursor broad phase). Silent without a base.
        if (types.perpendicular && base) {
            this.entities.forEach((data, id) => {
                if (data.type !== 'line') return;
                const a = data.start, b = data.end;
                const abx = b.x - a.x, aby = b.y - a.y;
                const len2 = abx * abx + aby * aby;
                if (len2 < 1e-12) return;
                const t = ((base.x - a.x) * abx + (base.y - a.y) * aby) / len2;
                const fx = a.x + abx * t, fy = a.y + aby * t, fz = a.z + (b.z - a.z) * t;
                const dSq = (fx - cursor.x) ** 2 + (fy - cursor.y) ** 2 + (fz - cursor.z) ** 2;
                if (dSq <= ap2) {
                    push('perpendicular', new THREE.Vector3(fx, fy, fz), id);
                }
            });
        }

        // ---- Per-object candidates (allocation only for near objects) ----
        for (const l of nearLines) {
            if (types.nearest) out.push({ type: 'nearest', position: l.foot.clone(), entityId: l.id, distance: Math.sqrt(l.footDistSq), priority: SNAP_PRIORITY.nearest });
        }
        for (const c of nearCurves) {
            if (types.nearest) {
                const p = nearestOnCircle(cursor, c.center, c.radius);
                if (p.distanceToSquared(cursor) <= ap2) push('nearest', p, c.id);
            }
            if (types.tangent) {
                const t = tangentFromPoint(cursor, c.center, c.radius);
                if (t && t.distanceToSquared(cursor) <= ap2) push('tangent', t, c.id);
            }
        }

        // ---- Pair candidates: near-subset only ----
        if (types.intersection) {
            for (let i = 0; i < nearLines.length; i++) {
                for (let j = i + 1; j < nearLines.length; j++) {
                    const p = segmentIntersect2(nearLines[i].a, nearLines[i].b, nearLines[j].a, nearLines[j].b);
                    if (p && p.distanceToSquared(cursor) <= ap2) push('intersection', p, nearLines[i].id);
                }
                for (const c of nearCurves) {
                    for (const p of lineCircleIntersect(nearLines[i].a, nearLines[i].b, c.center, c.radius)) {
                        if (p.distanceToSquared(cursor) <= ap2) push('intersection', p, nearLines[i].id);
                    }
                }
            }
            for (let i = 0; i < nearCurves.length; i++) {
                for (let j = i + 1; j < nearCurves.length; j++) {
                    for (const p of circleCircleIntersect(nearCurves[i].center, nearCurves[i].radius, nearCurves[j].center, nearCurves[j].radius)) {
                        if (p.distanceToSquared(cursor) <= ap2) push('intersection', p, nearCurves[i].id);
                    }
                }
            }
        }

        if (types.extension) {
            // Both endpoints carry an extension ray (a→b past b, b→a past a),
            // matching the original two-orientation pass.
            for (const l of extensionLines) {
                const pastB = extensionSnap(l.a, l.b, cursor, aperture);
                if (pastB) push('extension', pastB, l.id);
                const pastA = extensionSnap(l.b, l.a, cursor, aperture);
                if (pastA) push('extension', pastA, l.id);
            }
        }
    }

    /**
     * Get the best snap point (highest priority, closest)
     */
    getBestSnap(
        cursorWorld: THREE.Vector3,
        camera: THREE.Camera,
        renderer: THREE.WebGLRenderer,
        base?: THREE.Vector3 | null
    ): SnapPoint | null {
        const snaps = this.findSnapPoints(cursorWorld, camera, renderer, base);
        return snaps.length > 0 ? snaps[0] : null;
    }

    /**
     * Get snap points for a specific entity
     */
    private computeEntitySnapPoints(data: EntitySnapData, entityId: string): SnapPoint[] {
        return this.getEntitySnapPoints(data, entityId);
    }

    /**
     * Get snap points for a specific entity
     */
    private getEntitySnapPoints(data: EntitySnapData, entityId: string): SnapPoint[] {
        const points: SnapPoint[] = [];

        switch (data.type) {
            case 'line':
                points.push(...this.getLineSnaps(data as LineSnapData, entityId));
                break;
            case 'circle':
                points.push(...this.getCircleSnaps(data as CircleSnapData, entityId));
                break;
            case 'arc':
                points.push(...this.getArcSnaps(data as ArcSnapData, entityId));
                break;
            case 'rectangle':
                points.push(...this.getRectangleSnaps(data as RectangleSnapData, entityId));
                break;
            case 'box':
                points.push(...this.getBoxSnaps(data as BoxSnapData, entityId));
                break;
            case 'wall':
                points.push(...this.getWallSnaps(data as WallSnapData, entityId));
                break;
            case 'polyline': {
                // Vertex endpoints + per-segment midpoints: a polyline is
                // snapped as a chain of line snaps.
                const pd = data as PolylineSnapData;
                const vs = pd.vertices;
                const segCount = pd.closed ? vs.length : Math.max(vs.length - 1, 0);
                for (let i = 0; i < vs.length; i++) {
                    points.push({ type: 'endpoint', position: vs[i].clone(), entityId, distance: 0, priority: SNAP_PRIORITY.endpoint });
                }
                for (let i = 0; i < segCount; i++) {
                    const a = vs[i], b = vs[(i + 1) % vs.length];
                    points.push({ type: 'midpoint', position: a.clone().add(b).multiplyScalar(0.5), entityId, distance: 0, priority: SNAP_PRIORITY.midpoint });
                }
                break;
            }
            case 'point':
                points.push({
                    type: 'node',
                    position: new THREE.Vector3().copy((data as PointSnapData).position),
                    entityId,
                    distance: 0,
                    priority: SNAP_PRIORITY.node,
                });
                break;
        }

        return points;
    }

    /**
     * Line snap points: endpoints, midpoint
     */
    private getLineSnaps(data: LineSnapData, entityId: string): SnapPoint[] {
        const points: SnapPoint[] = [];

        // Endpoints
        points.push({
            type: 'endpoint',
            position: new THREE.Vector3().copy(data.start),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.endpoint,
        });
        points.push({
            type: 'endpoint',
            position: new THREE.Vector3().copy(data.end),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.endpoint,
        });

        // Midpoint
        const mid = new THREE.Vector3().addVectors(data.start, data.end).multiplyScalar(0.5);
        points.push({
            type: 'midpoint',
            position: mid,
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.midpoint,
        });

        return points;
    }

    /**
     * Circle snap points: center, quadrants
     */
    private getCircleSnaps(data: CircleSnapData, entityId: string): SnapPoint[] {
        const points: SnapPoint[] = [];

        // Center
        points.push({
            type: 'center',
            position: new THREE.Vector3().copy(data.center),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.center,
        });

        // Quadrants (0°, 90°, 180°, 270°)
        const quadrantAngles = [0, Math.PI / 2, Math.PI, 3 * Math.PI / 2];
        quadrantAngles.forEach((angle) => {
            const x = data.center.x + data.radius * Math.cos(angle);
            const y = data.center.y + data.radius * Math.sin(angle);
            points.push({
                type: 'quadrant',
                position: new THREE.Vector3(x, y, data.center.z),
                entityId,
                distance: 0,
                priority: SNAP_PRIORITY.quadrant,
            });
        });

        return points;
    }

    /**
     * Arc snap points: endpoints, center, midpoint
     */
    private getArcSnaps(data: ArcSnapData, entityId: string): SnapPoint[] {
        const points: SnapPoint[] = [];

        // Center
        points.push({
            type: 'center',
            position: new THREE.Vector3().copy(data.center),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.center,
        });

        // Start and end points
        const startX = data.center.x + data.radius * Math.cos(data.startAngle);
        const startY = data.center.y + data.radius * Math.sin(data.startAngle);
        points.push({
            type: 'endpoint',
            position: new THREE.Vector3(startX, startY, data.center.z),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.endpoint,
        });

        const endX = data.center.x + data.radius * Math.cos(data.endAngle);
        const endY = data.center.y + data.radius * Math.sin(data.endAngle);
        points.push({
            type: 'endpoint',
            position: new THREE.Vector3(endX, endY, data.center.z),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.endpoint,
        });

        // Midpoint of arc
        const midAngle = (data.startAngle + data.endAngle) / 2;
        const midX = data.center.x + data.radius * Math.cos(midAngle);
        const midY = data.center.y + data.radius * Math.sin(midAngle);
        points.push({
            type: 'midpoint',
            position: new THREE.Vector3(midX, midY, data.center.z),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.midpoint,
        });

        return points;
    }

    /**
     * Rectangle snap points: corners, midpoints, center
     */
    private getRectangleSnaps(data: RectangleSnapData, entityId: string): SnapPoint[] {
        const points: SnapPoint[] = [];
        const { corner1, corner2 } = data;

        // Corners (endpoints)
        // Z-up: corners vary in X/Y; both lie at corner1's level (Z).
        const corners = [
            new THREE.Vector3(corner1.x, corner1.y, corner1.z),
            new THREE.Vector3(corner2.x, corner1.y, corner1.z),
            new THREE.Vector3(corner2.x, corner2.y, corner1.z),
            new THREE.Vector3(corner1.x, corner2.y, corner1.z),
        ];

        corners.forEach((corner) => {
            points.push({
                type: 'endpoint',
                position: corner,
                entityId,
                distance: 0,
                priority: SNAP_PRIORITY.endpoint,
            });
        });

        // Edge midpoints
        for (let i = 0; i < 4; i++) {
            const mid = new THREE.Vector3()
                .addVectors(corners[i], corners[(i + 1) % 4])
                .multiplyScalar(0.5);
            points.push({
                type: 'midpoint',
                position: mid,
                entityId,
                distance: 0,
                priority: SNAP_PRIORITY.midpoint,
            });
        }

        // Center
        const center = new THREE.Vector3()
            .addVectors(corner1, corner2)
            .multiplyScalar(0.5);
        points.push({
            type: 'center',
            position: center,
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.center,
        });

        return points;
    }

    /**
     * Box snap points: 8 corners, edge midpoints, face centers, box center
     */
    private getBoxSnaps(data: BoxSnapData, entityId: string): SnapPoint[] {
        const points: SnapPoint[] = [];
        const { center, size } = data;
        const half = new THREE.Vector3(size.x / 2, size.y / 2, size.z / 2);

        // 8 corners
        const signs = [[-1, -1, -1], [1, -1, -1], [1, -1, 1], [-1, -1, 1],
        [-1, 1, -1], [1, 1, -1], [1, 1, 1], [-1, 1, 1]];

        signs.forEach(([sx, sy, sz]) => {
            points.push({
                type: 'endpoint',
                position: new THREE.Vector3(
                    center.x + sx * half.x,
                    center.y + sy * half.y,
                    center.z + sz * half.z
                ),
                entityId,
                distance: 0,
                priority: SNAP_PRIORITY.endpoint,
            });
        });

        // Box center
        points.push({
            type: 'center',
            position: new THREE.Vector3().copy(center),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.center,
        });

        return points;
    }

    /**
     * Wall snap points: endpoints of base line, midpoint
     */
    private getWallSnaps(data: WallSnapData, entityId: string): SnapPoint[] {
        const points: SnapPoint[] = [];

        // Wall endpoints (base line)
        points.push({
            type: 'endpoint',
            position: new THREE.Vector3().copy(data.start),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.endpoint,
        });
        points.push({
            type: 'endpoint',
            position: new THREE.Vector3().copy(data.end),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.endpoint,
        });

        // Midpoint
        const mid = new THREE.Vector3().addVectors(data.start, data.end).multiplyScalar(0.5);
        points.push({
            type: 'midpoint',
            position: mid,
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.midpoint,
        });

        return points;
    }

}

// ============================================================================
// ENTITY SNAP DATA TYPES
// ============================================================================

export interface BaseSnapData {
    type: string;
}

export interface PointSnapData extends BaseSnapData {
    type: 'point';
    position: THREE.Vector3;
}

export interface LineSnapData extends BaseSnapData {
    type: 'line';
    start: THREE.Vector3;
    end: THREE.Vector3;
}

export interface CircleSnapData extends BaseSnapData {
    type: 'circle';
    center: THREE.Vector3;
    radius: number;
}

export interface ArcSnapData extends BaseSnapData {
    type: 'arc';
    center: THREE.Vector3;
    radius: number;
    startAngle: number;
    endAngle: number;
}

export interface RectangleSnapData extends BaseSnapData {
    type: 'rectangle';
    corner1: THREE.Vector3;
    corner2: THREE.Vector3;
}

export interface BoxSnapData extends BaseSnapData {
    type: 'box';
    center: THREE.Vector3;
    size: THREE.Vector3;
}

export interface WallSnapData extends BaseSnapData {
    type: 'wall';
    start: THREE.Vector3;
    end: THREE.Vector3;
    height: number;
}

export interface PolylineSnapData extends BaseSnapData {
    type: 'polyline';
    vertices: THREE.Vector3[];
    closed?: boolean;
}

export type EntitySnapData =
    | PointSnapData
    | LineSnapData
    | CircleSnapData
    | ArcSnapData
    | RectangleSnapData
    | BoxSnapData
    | WallSnapData
    | PolylineSnapData;

// ============================================================================
// SINGLETON MANAGER INSTANCE
// ============================================================================

let snapManagerInstance: SnapManager | null = null;

export function getSnapManager(): SnapManager {
    if (!snapManagerInstance) {
        const state = useSnapStore.getState();
        snapManagerInstance = new SnapManager({
            enabled: state.enabled,
            aperture: state.aperture,
            gridSize: state.gridSize,
            gridSnapEnabled: state.gridSnapEnabled,
            types: state.types,
        });
    }
    return snapManagerInstance;
}

export function resetSnapManager(): void {
    snapManagerInstance = null;
}

// ============================================================================
// PIXEL → WORLD CONVERSION (shared with pixel-fixed overlays)
// ============================================================================

const _pxToWorldSize = new THREE.Vector2();

/**
 * Convert a pixel distance to world units at the given point's depth.
 * Zoom-aware: the app zooms the orthographic plan cameras through
 * `camera.zoom` while the frustum top/bottom stay fixed, so the frustum
 * height must be divided by zoom — an aperture measured without it inflates
 * by the zoom factor and snap targets grab the cursor from far away when
 * zoomed in (and go deaf when zoomed out).
 */
export function pixelsToWorld(
    pixels: number,
    point: THREE.Vector3,
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer
): number {
    const size = renderer.getSize(_pxToWorldSize);
    const zoom = (camera as THREE.OrthographicCamera).zoom || 1;

    if (camera instanceof THREE.PerspectiveCamera) {
        const distance = camera.position.distanceTo(point);
        const vFov = (camera.fov * Math.PI) / 180;
        const worldHeight = 2 * distance * Math.tan(vFov / 2);
        return (pixels / size.y) * (worldHeight / zoom);
    }
    // Orthographic camera
    const ortho = camera as THREE.OrthographicCamera;
    return (pixels / size.y) * ((ortho.top - ortho.bottom) / zoom);
}

// ============================================================================
// DYNAMIC-SNAP GEOMETRY HELPERS (cursor-relative candidate computation)
// ============================================================================

// The per-segment perpendicular foot (nearest/perpendicular candidates) is
// computed inline by collectDynamicSnaps' scalar broad phase; the old
// closestOnSegment3 helper became dead when the pair loops were gated.

/** Nearest point on a circle's rim to `p` (in XY, level preserved). */
function nearestOnCircle(p: THREE.Vector3, center: THREE.Vector3, radius: number): THREE.Vector3 {
    const dx = p.x - center.x, dy = p.y - center.y;
    const d = Math.hypot(dx, dy) || 1;
    return new THREE.Vector3(center.x + (dx / d) * radius, center.y + (dy / d) * radius, center.z);
}

/** Tangent point on a circle (XY) from an external point `p`. Null inside. */
function tangentFromPoint(p: THREE.Vector3, center: THREE.Vector3, radius: number): THREE.Vector3 | null {
    const dx = p.x - center.x, dy = p.y - center.y;
    const d2 = dx * dx + dy * dy, r2 = radius * radius;
    if (d2 < r2) return null;
    const a = r2 / d2, b = radius * Math.sqrt(Math.max(0, d2 - r2)) / d2;
    const t1x = center.x + a * dx - b * dy, t1y = center.y + a * dy + b * dx;
    const t2x = center.x + a * dx + b * dy, t2y = center.y + a * dy - b * dx;
    const d1 = (t1x - p.x) ** 2 + (t1y - p.y) ** 2;
    const d2b = (t2x - p.x) ** 2 + (t2y - p.y) ** 2;
    return d1 <= d2b
        ? new THREE.Vector3(t1x, t1y, center.z)
        : new THREE.Vector3(t2x, t2y, center.z);
}

/** Finite-segment 2D intersection (XY), or null. */
function segmentIntersect2(a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, d: THREE.Vector3): THREE.Vector3 | null {
    const r = { x: b.x - a.x, y: b.y - a.y };
    const s = { x: d.x - c.x, y: d.y - c.y };
    const denom = r.x * s.y - r.y * s.x;
    if (Math.abs(denom) < 1e-9) return null;
    const t = ((c.x - a.x) * s.y - (c.y - a.y) * s.x) / denom;
    const u = ((c.x - a.x) * r.y - (c.y - a.y) * r.x) / denom;
    const tol = 1e-9;
    if (t < -tol || t > 1 + tol || u < -tol || u > 1 + tol) return null;
    return new THREE.Vector3(a.x + t * r.x, a.y + t * r.y, a.z + (b.z - a.z) * t);
}

/** Points where a finite segment (XY) crosses a circle. */
function lineCircleIntersect(a: THREE.Vector3, b: THREE.Vector3, center: THREE.Vector3, radius: number): THREE.Vector3[] {
    const dx = b.x - a.x, dy = b.y - a.y;
    const fx = a.x - center.x, fy = a.y - center.y;
    const A = dx * dx + dy * dy;
    if (A < 1e-12) return [];
    const B = 2 * (fx * dx + fy * dy);
    const C = fx * fx + fy * fy - radius * radius;
    const disc = B * B - 4 * A * C;
    if (disc < 0) return [];
    const sq = Math.sqrt(disc);
    const ts = [(-B - sq) / (2 * A), (-B + sq) / (2 * A)].filter((t) => t >= -1e-9 && t <= 1 + 1e-9);
    return ts.map((t) => new THREE.Vector3(a.x + dx * t, a.y + dy * t, center.z));
}

/** Two-circle intersection points (XY). */
function circleCircleIntersect(c1: THREE.Vector3, r1: number, c2: THREE.Vector3, r2: number): THREE.Vector3[] {
    const dx = c2.x - c1.x, dy = c2.y - c1.y;
    const d = Math.hypot(dx, dy);
    if (d < 1e-9 || d > r1 + r2 || d < Math.abs(r1 - r2)) return [];
    const a = (r1 * r1 - r2 * r2 + d * d) / (2 * d);
    const h = Math.sqrt(Math.max(0, r1 * r1 - a * a));
    const mx = c1.x + a * dx / d, my = c1.y + a * dy / d;
    const rx = -dy / d * h, ry = dx / d * h;
    const out = [new THREE.Vector3(mx + rx, my + ry, c1.z), new THREE.Vector3(mx - rx, my - ry, c1.z)];
    return h < 1e-6 ? [out[0]] : out;
}

/**
 * Extension snap: the cursor projects onto the ray continuing past segment
 * endpoint `b` (away from `a`); returns the foot when it is past b (not on
 * the segment itself) and within `aperture`, like the classic EXT object snap.
 */
function extensionSnap(a: THREE.Vector3, b: THREE.Vector3, cursor: THREE.Vector3, aperture: number): THREE.Vector3 | null {
    const dir = new THREE.Vector2(b.x - a.x, b.y - a.y);
    if (dir.lengthSq() < 1e-12) return null;
    dir.normalize();
    const rel = new THREE.Vector2(cursor.x - b.x, cursor.y - b.y);
    const along = rel.dot(dir);
    if (along < 0) return null; // cursor is not past the b endpoint
    const perp = new THREE.Vector2(-dir.y, dir.x);
    const dist = Math.abs(rel.dot(perp));
    if (dist > aperture) return null;
    return new THREE.Vector3(b.x + dir.x * along, b.y + dir.y * along, b.z);
}
